import {
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@material-ui/core';
import { HighlightOff, SettingsBackupRestore } from '@material-ui/icons';
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import { api } from '@services/api';
import MuiAlert, { AlertProps } from '@material-ui/lab/Alert';
import { Button } from '@components/Button';
import { Pagination } from '@material-ui/lab';
import { ContainerButtonsTop, DivSearch } from './styles';
import { NavLink } from '@components/NavLink';
import { MagnifyingGlass, X, Plus } from '@phosphor-icons/react';
import { status } from '@data/status';
import { maskCPF, verifyNumberOrString } from '@utils/masks';
import { AppError } from '@utils/AppError';

interface IAgenda {
  id_agendamento: number;
  nome: string;
  cpf_colaborador: string;
  tipo_agendamento: string;
  data_agendamento: string;
  horario: string;
  status: string;
  nome_comercial: string;
}

interface PropsResult {
  results: IAgenda[];
  total_pages: number;
  total_records?: number;
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  }
);

const PER_PAGE = 30;

export const Agendamentos: React.FC = () => {
  const [data, setData] = useState<IAgenda[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [filterState, setFilterState] = useState('');
  const [dialogDelete, setDialogDelete] = useState(false);
  const [edit, setEdit] = useState<IAgenda | null>(null);
  const [snack, setSnack] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<
    'success' | 'error' | 'info' | 'warning'
  >('success');
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [messageEmpty, setMessageEmpty] = useState('');

  const fetchData = async (
    page: number,
    search: string = '',
    status: string = ''
  ) => {
    try {
      setLoading(true);
      const params = {
        page,
        ...(search && { search }),
        ...(status && { status })
      };

      const response = await api.get(`ravim/agendamento`, { params });

      setData(response.data.results);
      setTotalPages(response.data.total_pages);
      setTotalRecords(response.data.total_records || 0);
    } catch (error) {
      console.error('Erro ao carregar:', error);
      setSnack(true);
      setMessage('Erro ao carregar dados');
      setSeverity('error');
    } finally {
      setLoading(false);
    }
  };

  const fetchFilter = useCallback(async (value: string, status: string) => {
    try {
      setIsLoadingData(true);
      setMessageEmpty('');

      const name = value.includes('.') ? '' : value.toUpperCase().trim();
      const state = status.toUpperCase().trim();
      const cpf = value.includes('.') ? value : '';

      const data = {
        nome: name,
        status: state,
        cpf
      };

      const response = await api.post('ravim/pesq-agenda', data);

      if (response.data.length > 0) {
        setData(response.data);
        setTotalPages(1);
        setCurrentPage(1);
      } else {
        setMessageEmpty('Registro não encontrado.');
        setData([]);
      }
    } catch (error) {
      if (error instanceof AppError) {
        if (error.code === 404) {
          setMessageEmpty(error.message);
          setData([]);
        }
      }
    } finally {
      setIsLoadingData(false);
    }
  }, []);

  useEffect(() => {
    if (searchValue || filterState) {
      fetchFilter(searchValue, filterState);
    } else {
      fetchData(currentPage);
    }
  }, [currentPage, searchValue, filterState]);

  const handlePageChange = (event: unknown, newPage: number) => {
    setCurrentPage(newPage);
    window.scrollTo(0, 0);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (verifyNumberOrString(value)) {
      setSearchValue(maskCPF(value));
    } else {
      setSearchValue(value);
    }
  };

  const handleClearSearch = () => {
    setSearchValue('');
    setFilterState('');
    setMessageEmpty('');
    setCurrentPage(1);
  };

  const deleteAgendamento = (agenda: IAgenda) => {
    setEdit(agenda);
    setDialogDelete(true);
  };

  const closeDialog = () => {
    setDialogDelete(false);
    setEdit(null);
  };

  const deleteAgenda = async () => {
    if (!edit) return;

    try {
      const res = await api.delete(`/ravim/exclu-agendamento`, {
        data: { id_agendamento: String(edit.id_agendamento) }
      });

      if (res.status === 200) {
        setSnack(true);
        setMessage(res.data.message);
        setSeverity('success');
        setDialogDelete(false);
        fetchData(currentPage, searchValue, filterState);
      }
    } catch (err: any) {
      setSnack(true);
      setMessage(err.response?.data?.message || 'Erro ao excluir');
      setSeverity('error');
    }
  };

  const handleSnackClose = () => {
    setSnack(false);
  };

  const formatDate = (dateString: string) => {
    return dateString.replace(/(\d*)-(\d*)-(\d*).*/, '$3/$2/$1');
  };

  return (
    <Container>
      <ContainerButtonsTop>
        <DivSearch>
          <TextField
            label="Pesquisar agendamento"
            type="text"
            variant="outlined"
            value={searchValue}
            inputProps={{
              maxLength: verifyNumberOrString(searchValue) ? 14 : null
            }}
            onChange={handleSearchChange}
            fullWidth
          />
          <FormControl variant="outlined" style={{ minWidth: 200 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={filterState}
              onChange={(e: any) => setFilterState(e.target.value)}
            >
              {status.map((statusItem) => (
                <MenuItem key={statusItem.value} value={statusItem.value}>
                  {statusItem.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variantstyle="SECUNDARY"
            isLoading={isLoadingData}
            icon={<MagnifyingGlass size={20} />}
            disabled={!searchValue && !filterState}
            onClick={() => fetchFilter(searchValue, filterState)}
          />
          {(searchValue || filterState) && (
            <Button
              variantstyle="RED"
              icon={<X size={20} />}
              onClick={handleClearSearch}
            />
          )}
        </DivSearch>
      </ContainerButtonsTop>

      <Typography
        variant="h4"
        align="center"
        style={{ marginBottom: 20, fontWeight: 'bold' }}
      >
        Agendamentos
      </Typography>

      <Box display="flex" justifyContent="center" marginBottom={2}>
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
          size="large"
          showFirstButton
          showLastButton
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Colaborador</strong>
              </TableCell>
              <TableCell>
                <strong>CPF</strong>
              </TableCell>
              <TableCell>
                <strong>Nome Comercial</strong>
              </TableCell>
              <TableCell>
                <strong>Status</strong>
              </TableCell>
              <TableCell>
                <strong>Data</strong>
              </TableCell>
              <TableCell>
                <strong>Horário</strong>
              </TableCell>
              <TableCell>
                <strong>Tipo</strong>
              </TableCell>
              <TableCell>
                <strong>Ações</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  align="center"
                  style={{ padding: '40px 0' }}
                >
                  <CircularProgress size={40} />
                  <Typography variant="body1" style={{ marginTop: 16 }}>
                    Carregando Agendamentos...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : messageEmpty ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  {messageEmpty}
                </TableCell>
              </TableRow>
            ) : data.length > 0 ? (
              data.map((item) => (
                <TableRow key={item.id_agendamento}>
                  <TableCell>{item.nome}</TableCell>
                  <TableCell>{maskCPF(item.cpf_colaborador)}</TableCell>
                  <TableCell>{item.nome_comercial}</TableCell>
                  <TableCell>{item.status}</TableCell>
                  <TableCell>{formatDate(item.data_agendamento)}</TableCell>
                  <TableCell>{item.horario}</TableCell>
                  <TableCell>{item.tipo_agendamento}</TableCell>
                  <TableCell>
                    <Box display="flex">
                      <Link
                        to={{
                          pathname: `/editar-agendamento/${item.id_agendamento}`,
                          state: item
                        }}
                      >
                        <IconButton aria-label="editar">
                          <SettingsBackupRestore color="primary" />
                        </IconButton>
                      </Link>
                      <IconButton
                        aria-label="deletar"
                        onClick={() => deleteAgendamento(item)}
                      >
                        <HighlightOff color="error" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Nenhum agendamento encontrado
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" justifyContent="center" marginTop={2}>
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
          size="large"
          showFirstButton
          showLastButton
        />
      </Box>

      <Dialog open={dialogDelete} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Você está prestes a excluir o seguinte agendamento:
          </Typography>
          <Typography>
            <strong>Nome:</strong> {edit?.nome || '-'}
          </Typography>
          <Typography>
            <strong>Data:</strong>{' '}
            {edit?.data_agendamento ? formatDate(edit.data_agendamento) : '-'}
          </Typography>
          <Typography>
            <strong>Horário:</strong> {edit?.horario || '-'}
          </Typography>
          <Typography color="error" style={{ marginTop: 16 }}>
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeDialog}
            title="Cancelar"
            variantstyle="SECUNDARY"
          />
          <Button
            onClick={deleteAgenda}
            title="Confirmar Exclusão"
            variantstyle="RED"
            autoFocus
          />
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack}
        autoHideDuration={6000}
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackClose} severity={severity}>
          {message}
        </Alert>
      </Snackbar>
    </Container>
  );
};
